// src/App.js - גרסה מעודכנת עם יצירת משתמש אוטומטית
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  VStack, 
  Heading, 
  Button, 
  Center, 
  Spinner, 
  HStack, 
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertDescription,
  Container,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
  useToast
} from '@chakra-ui/react';
import Login from './components/Login';
import Contacts from './components/Contacts';
import CallSection from './components/CallSection';
import AdminPanel from './components/AdminPanel';
import { auth, db } from './firebase';
import { getUser } from './firebase/userService';
import { 
  doc, 
  getDoc, 
  setDoc, 
  serverTimestamp 
} from 'firebase/firestore';

// הגדרת כתובת השרת
const serverUrl = 'https://test.cs1118.cloud-node.net';

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serverStatus, setServerStatus] = useState('checking');
  const [userRole, setUserRole] = useState(null);
  const [userRoleLoading, setUserRoleLoading] = useState(false);
  const toast = useToast();

  // צבעים עבור Dark/Light mode
  const bgColor = useColorModeValue('gray.50', 'gray.900');

  // פונקציה ליצירת/עדכון משתמש ב-Firestore
  const ensureUserExists = async (user) => {
    if (!user) return null;

    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        // console.log('Creating new user document for:', user.email);
        
        const userData = {
          // Basic info
          email: user.email || '',
          name: user.displayName || '',
          phone: user.phoneNumber || '',
          role: 'user',
          
          // Financial
          credits: 0, // מתחיל ב-0 קרדיט
          total_spent: 0,
          
          // Features - תכונות מופעלות
          features: {
            unlimited_calls: false,
            priority_support: false,
            analytics: false,
            bulk_operations: false,
            custom_messages: false,
            advanced_routing: false,
            api_access: false,
            white_label: false,
            digging_mode: false // מצב חפירה כבוי כברירת מחדל - רק אדמין יכול להפעיל
          },
          
          // Statistics
          stats: {
            total_calls: 0,
            successful_calls: 0,
            failed_calls: 0,
            total_minutes: 0,
            last_call: null,
            avg_call_duration: 0,
            monthly_calls: 0,
            monthly_spent: 0
          },
          
          // Settings
          settings: {
            max_attempts: 3,
            call_timeout: 30,
            retry_delay: 5,
            default_from_number: '',
            notification_email: true,
            notification_sms: false
          },
          
          // Subscription info
          subscription: {
            plan: 'basic',
            status: 'active',
            expires_at: null,
            auto_renew: false
          },
          
          // Timestamps
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          last_login: new Date().toISOString()
        };
        
        await setDoc(userRef, userData);
        // console.log('User document created successfully for:', user.email);
        
        // הודעת הצלחה
        toast({
          title: '🎉 פרופיל נוצר בהצלחה!',
          description: `ברוכ${user.displayName ? ' ' + user.displayName : ''} הבא למערכת! פרופיל המשתמש שלך הוכן.`,
          status: 'success',
          duration: 4000,
          isClosable: true
        });
        
        return userData;
      } else {
        // עדכון last_login אם המשתמש כבר קיים
        const existingData = userDoc.data();
        await setDoc(userRef, {
          ...existingData,
          last_login: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }, { merge: true });
        
        // console.log('Updated last_login for existing user:', user.email);
        return existingData;
      }
    } catch (error) {
      console.error('Error ensuring user exists:', error);
      toast({
        title: 'שגיאה ביצירת פרופיל',
        description: 'אירעה שגיאה ביצירת פרופיל המשתמש. אנא נסה שוב.',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
      return null;
    }
  };

  // בדיקת אימות משתמש - עם יצירת משתמש אוטומטית
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      // console.log('Auth state changed:', user ? user.email : 'null');
      
      if (user) {
        // יצירת/עדכון משתמש ב-Firestore
        try {
          await ensureUserExists(user);
          setIsAuthenticated(true);
          // console.log('User authenticated and document ensured for:', user.email);
        } catch (error) {
          console.error('Error during user setup:', error);
          setIsAuthenticated(false);
          toast({
            title: 'שגיאה באימות',
            description: 'אירעה שגיאה באימות המשתמש',
            status: 'error',
            duration: 3000,
            isClosable: true
          });
        }
      } else {
        setIsAuthenticated(false);
        setUserRole(null);
        setSelectedContacts([]);
      }
      
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [toast]);

  // בדיקת רול משתמש
  useEffect(() => {
    const checkUserRole = async () => {
      if (isAuthenticated && auth.currentUser) {
        setUserRoleLoading(true);
        try {
          // console.log('Checking user role for:', auth.currentUser.email);
          const userData = await getUser(auth.currentUser.uid);
          const role = userData?.role || 'user';
          setUserRole(role);
          // console.log(`User role loaded: ${role} for ${auth.currentUser.email}`);
        } catch (error) {
          console.error('Error checking user role:', error);
          setUserRole('user');
        } finally {
          setUserRoleLoading(false);
        }
      }
    };
    
    checkUserRole();
  }, [isAuthenticated]);

  // בדיקת סטטוס השרת
  useEffect(() => {
    const checkServerStatus = async () => {
      try {
        const response = await fetch(`${serverUrl}/api`);
        if (response.ok) {
          const data = await response.json();
          setServerStatus(data.realtime ? 'connected' : 'partial');
        } else {
          setServerStatus('error');
        }
      } catch (error) {
        console.error('Server check failed:', error);
        setServerStatus('offline');
      }
    };

    if (isAuthenticated) {
      checkServerStatus();
      // בדיקה כל 30 שניות
      const interval = setInterval(checkServerStatus, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      setIsAuthenticated(false);
      setSelectedContacts([]);
      setUserRole(null);
      toast({
        title: 'התנתקת בהצלחה',
        description: 'להתראות!',
        status: 'info',
        duration: 2000,
        isClosable: true
      });
    } catch (error) {
      console.error('Error signing out:', error);
      toast({
        title: 'שגיאה בהתנתקות',
        description: 'אירעה שגיאה בהתנתקות',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    }
  };

  const getServerStatusAlert = () => {
    switch (serverStatus) {
      case 'connected':
        return (
          <Alert status="success" borderRadius="md" mb={4}>
            <AlertIcon />
            <AlertDescription>
              מחובר לשרת - מערכת פעילה ומוכנה לעבודה
            </AlertDescription>
          </Alert>
        );
      case 'partial':
        return (
          <Alert status="warning" borderRadius="md" mb={4}>
            <AlertIcon />
            <AlertDescription>
              מחובר לשרת - אין תמיכה בעדכונים בזמן אמת
            </AlertDescription>
          </Alert>
        );
      case 'offline':
        return (
          <Alert status="error" borderRadius="md" mb={4}>
            <AlertIcon />
            <AlertDescription>
              שרת לא זמין - פונקציונליות מוגבלת
            </AlertDescription>
          </Alert>
        );
      case 'error':
        return (
          <Alert status="error" borderRadius="md" mb={4}>
            <AlertIcon />
            <AlertDescription>
              שגיאה בחיבור לשרת - נסה לרענן את הדף
            </AlertDescription>
          </Alert>
        );
      default:
        return null;
    }
  };

  // טעינה ראשונית
  if (loading) {
    return (
      <Center h="100vh" bg={bgColor}>
        <VStack spacing={4}>
          <Spinner size="xl" color="teal.500" thickness="4px" />
          <Heading size="md" color="gray.600">טוען...</Heading>
        </VStack>
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor}>
      <Container maxW="container.xl" py={8}>
        {isAuthenticated ? (
          <>
            {/* כותרת עליונה */}
            <HStack justifyContent="space-between" mb={6} flexWrap="wrap">
              <HStack spacing={3}>
                <Heading size="lg" color="teal.600">
                  מערכת שיחות מתקדמת
                </Heading>
                {userRole === 'admin' && (
                  <Badge colorScheme="red" fontSize="sm" px={3} py={1} borderRadius="full">
                    מנהל מערכת
                  </Badge>
                )}
                {userRoleLoading && (
                  <Spinner size="sm" color="teal.500" />
                )}
              </HStack>
              <HStack spacing={3}>
                {auth.currentUser && (
                  <Badge colorScheme="blue" fontSize="sm" px={3} py={1} borderRadius="full">
                    {auth.currentUser.email}
                  </Badge>
                )}
                <Button 
                  onClick={handleLogout}
                  colorScheme="red"
                  variant="outline"
                  size="md"
                >
                  התנתק
                </Button>
              </HStack>
            </HStack>

            {/* התראת סטטוס שרת */}
            {getServerStatusAlert()}

            {/* תוכן ראשי - אדמין או משתמש רגיל */}
            {userRole === 'admin' ? (
              // ממשק אדמין עם טאבים
              <Tabs variant="enclosed" colorScheme="teal" size="lg">
                <TabList>
                  <Tab>
                    <VStack spacing={1}>
                      <Box>מערכת שיחות</Box>
                      <Box fontSize="xs" color="gray.500">ביצוע שיחות</Box>
                    </VStack>
                  </Tab>
                  <Tab>
                    <VStack spacing={1}>
                      <Box>פאנל אדמין</Box>
                      <Box fontSize="xs" color="gray.500">ניהול מערכת</Box>
                    </VStack>
                  </Tab>
                </TabList>
                <TabPanels>
                  <TabPanel>
                    <VStack spacing={8} align="stretch">
                      <Contacts onContactSelect={setSelectedContacts} />
                      <CallSection 
                        selectedContacts={selectedContacts} 
                        serverUrl={serverUrl}
                        serverStatus={serverStatus}
                      />
                    </VStack>
                  </TabPanel>
                  <TabPanel>
                    <AdminPanel />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            ) : (
              // ממשק משתמש רגיל
              <VStack spacing={8} align="stretch">
                <Contacts onContactSelect={setSelectedContacts} />
                <CallSection 
                  selectedContacts={selectedContacts} 
                  serverUrl={serverUrl}
                  serverStatus={serverStatus}
                />
              </VStack>
            )}
          </>
        ) : (
          // מסך התחברות
          <Center h="100vh">
            <Box w="100%" maxW="md">
              <Login onLogin={handleLogin} />
            </Box>
          </Center>
        )}
      </Container>
    </Box>
  );
};

export default App;